// Utility functions for displaying agency and role information
// Based on enhanced backend API response structure

interface UserWithAgencyData {
  agencyRole?: string;
  title?: string | null; // User's custom title
  primaryAgency?: {
    id: string;
    name: string;
  };
  isIndependent?: boolean;
  currentAgency?: {
    agency: {
      id: string;
      name: string;
    };
    role: string;
    agencyRole?: string; // Backend compatibility field
  };
}

interface AgencyWithRole {
  agency: {
    id: string;
    name: string;
  };
  role: string;
}

/**
 * Display agency and role based on enhanced backend API structure
 * Format: "Agency Name - Role (Custom Title)" or "Agency Name - Role" if no title
 * @param user - User object with enhanced agency data
 * @returns Formatted agency and role string
 */
export function displayAgencyRole(user: UserWithAgencyData): string {
  // Handle independent agents
  if (user.isIndependent) {
    return "Independent Agent";
  }

  // Use primary agency and role from enhanced API
  if (user.primaryAgency && user.agencyRole) {
    const normalizedRole = normalizeRole(user.agencyRole);
    const capitalizedRole = capitalizeRole(normalizedRole);

    // Add custom title if available
    if (user.title && user.title.trim() !== "") {
      return `${user.primaryAgency.name} - ${capitalizedRole} (${user.title})`;
    }

    return `${user.primaryAgency.name} - ${capitalizedRole}`;
  }

  // Main path: Use current agency structure with proper role
  if (user.currentAgency && user.currentAgency.agency) {
    const agencyName = user.currentAgency.agency.name;
    const role = user.currentAgency.role || user.currentAgency.agencyRole;

    if (role && role.trim() !== "") {
      const normalizedRole = normalizeRole(role);
      const capitalizedRole = capitalizeRole(normalizedRole);

      // Add custom title if available
      if (user.title && user.title.trim() !== "") {
        return `${agencyName} - ${capitalizedRole} (${user.title})`;
      }

      return `${agencyName} - ${capitalizedRole}`;
    }

    // Fallback: Agency name with default role if no role provided
    if (user.title && user.title.trim() !== "") {
      return `${agencyName} - Agent (${user.title})`;
    }

    return `${agencyName} - Agent`;
  }

  // Fallback to just role if available from enhanced API
  if (user.agencyRole) {
    const normalizedRole = normalizeRole(user.agencyRole);
    const capitalizedRole = capitalizeRole(normalizedRole);

    if (user.title && user.title.trim() !== "") {
      return `${capitalizedRole} (${user.title})`;
    }

    return capitalizedRole;
  }

  // Final fallback
  return "Agent";
}

/**
 * Normalize role names to match database constraints
 * @param role - Raw role from backend
 * @returns Normalized role string
 */
function normalizeRole(role: string): string {
  // Map backend role names to database constraint values
  const roleMap: Record<string, string> = {
    'agency_owner': 'owner',
    'agency owner': 'owner',
    'agency admin': 'admin',
    'agency agent': 'agent',
    'agency viewer': 'viewer',
  };

  return roleMap[role.toLowerCase()] || role;
}

/**
 * Capitalize role for display
 * @param role - Role string to capitalize
 * @returns Capitalized role string
 */
function capitalizeRole(role: string): string {
  if (role.toLowerCase() === 'admin') return 'Agency Admin';
  return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
}

/**
 * Display agency name with role in parentheses for agency selection modals
 * @param agency - Agency with role information
 * @returns Formatted string
 */
export function displayAgencyWithRole(agency: AgencyWithRole): string {
  if (agency.role) {
    const normalizedRole = normalizeRole(agency.role);
    return `${agency.agency.name} (${normalizedRole})`;
  }
  return agency.agency.name;
}

/**
 * Get role display for current agency context
 * @param user - User object with agency data
 * @returns Role string or empty string
 */
export function getCurrentAgencyRole(user: UserWithAgencyData): string {
  return user.agencyRole || user.currentAgency?.role || "";
}

/**
 * Check if user is independent (has no agency)
 * @param user - User object with agency data
 * @returns True if user is independent
 */
export function isIndependentAgent(user: UserWithAgencyData): boolean {
  return Boolean(user.isIndependent);
}